import Phaser from 'phaser'

declare const __ASSET_VER__: string

const LS_BGM = '10block_bgm'           // on/off
const LS_SFX = '10block_sfx'           // on/off
const LS_BGM_MODE = '10block_bgm_mode' // auto | manual | random
const LS_BGM_HOUR = '10block_bgm_hour' // manual 선택 시각(0~23)
const LS_BGM_VOL = '10block_bgm_vol'   // 0~1
const LS_SFX_VOL = '10block_sfx_vol'   // 0~1 (효과음 마스터 배수)

export type BgmMode = 'auto' | 'manual' | 'random'

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

class AudioManagerImpl {
  private scene?: Phaser.Scene
  private bgm?: Phaser.Sound.BaseSound
  private currentKey?: string
  private bgmOn = true
  private sfxOn = true
  private bgmVolume = 0.3 // 시간대 트랙 기본 볼륨
  private sfxVolume = 1 // 효과음 마스터 배수(play의 개별 볼륨에 곱)
  private bgmMode: BgmMode = 'auto'
  private manualHour = 0
  private hourTimer?: ReturnType<typeof setInterval>

  constructor() {
    try {
      this.bgmOn = localStorage.getItem(LS_BGM) !== 'off'
      this.sfxOn = localStorage.getItem(LS_SFX) !== 'off'
      const m = localStorage.getItem(LS_BGM_MODE)
      if (m === 'auto' || m === 'manual' || m === 'random') this.bgmMode = m
      const h = parseInt(localStorage.getItem(LS_BGM_HOUR) ?? '', 10)
      if (!Number.isNaN(h)) this.manualHour = ((h % 24) + 24) % 24
      const bv = parseFloat(localStorage.getItem(LS_BGM_VOL) ?? '')
      if (!Number.isNaN(bv)) this.bgmVolume = clamp01(bv)
      const sv = parseFloat(localStorage.getItem(LS_SFX_VOL) ?? '')
      if (!Number.isNaN(sv)) this.sfxVolume = clamp01(sv)
    } catch {
      /* localStorage 사용 불가 환경 무시 */
    }
  }

  init(scene: Phaser.Scene) { this.scene = scene }

  isBgmOn() { return this.bgmOn }
  isSfxOn() { return this.sfxOn }
  getBgmMode(): BgmMode { return this.bgmMode }
  getManualHour() { return this.manualHour }
  /** 현재 실제 재생 중인 시간대 트랙의 시각(0~23). 재생 중이 아니거나 시간대 트랙이 아니면 null. */
  getCurrentHour(): number | null {
    if (!this.bgmOn || !this.bgm?.isPlaying) return null
    const m = this.currentKey?.match(/^bgm_hour_(\d{2})$/)
    return m ? parseInt(m[1], 10) : null
  }

  /** 현재(또는 선택된) 시각 곡의 한국어 라벨. 예: "낮 2시" */
  getCurrentTrackName(): string {
    const h = this.getCurrentHour() ?? (this.bgmMode === 'manual' ? this.manualHour : new Date().getHours()) ?? 0
    const hr = ((h % 24) + 24) % 24
    const period = hr < 6 ? '새벽' : hr < 12 ? '아침' : hr < 18 ? '낮' : '저녁'
    const h12 = hr % 12 === 0 ? 12 : hr % 12
    return `${period} ${h12}시`
  }

  /** 미니 플레이어: 다음 곡(랜덤 모드면 새 랜덤, 아니면 다음 시각). 꺼져 있으면 켜짐. */
  nextTrack() {
    if (this.bgmMode === 'random') { this.setBgmMode('random'); return }
    const base = this.getCurrentHour() ?? this.manualHour ?? new Date().getHours()
    this.setBgmMode('manual', (base + 1) % 24)
  }

  /** 미니 플레이어: 이전 곡. */
  prevTrack() {
    if (this.bgmMode === 'random') { this.setBgmMode('random'); return }
    const base = this.getCurrentHour() ?? this.manualHour ?? new Date().getHours()
    this.setBgmMode('manual', (base + 23) % 24)
  }
  getBgmVolume() { return this.bgmVolume }
  getSfxVolume() { return this.sfxVolume }

  /** 효과음 재생(켜져 있을 때만). vol = 사운드별 상대 볼륨 × sfxVolume(마스터). */
  play(key: string, vol = 0.35) {
    if (this.sfxOn) this.scene?.sound.play(key, { volume: vol * this.sfxVolume })
  }

  /** 비-시간대 트랙(예: bgm_result) 직접 재생. Boot에서 미리 로드된 키만. */
  playBgm(key: string) {
    if (!this.scene || !this.bgmOn) { this.currentKey = key; return }
    if (this.currentKey === key && this.bgm?.isPlaying) return
    this.bgm?.stop(); this.bgm?.destroy()
    this.bgm = this.scene.sound.add(key, { loop: true, volume: this.bgmVolume })
    this.currentKey = key
    this.bgm.play()
  }

  /** 씬 진입 시 호출: 현재 설정(모드/on)대로 BGM 재생. 같은 트랙이면 끊지 않음. */
  startBgm() {
    if (!this.scene || !this.bgmOn) return
    this.ensureHourWatcher()
    if (this.bgmMode === 'random') { this.playRandom(); return }
    const hour = this.bgmMode === 'manual' ? this.manualHour : new Date().getHours()
    this.playHour(hour, true)
  }

  private playHour(hour: number, loop: boolean, onComplete?: () => void) {
    const key = `bgm_hour_${String(((hour % 24) + 24) % 24).padStart(2, '0')}`
    if (loop && key === this.currentKey && this.bgm?.isPlaying) return // 같은 곡 유지(씬 전환 끊김 방지)
    this.loadThenPlay(key, loop, onComplete)
  }

  /** 랜덤: 재생 중이 아니면 무작위 곡 시작. 곡이 끝나면 다음 무작위 곡으로(셔플). */
  private playRandom() {
    if (this.bgm?.isPlaying) return
    this.startRandomTrack()
  }

  private startRandomTrack() {
    this.playHour(Math.floor(Math.random() * 24), false, () => {
      if (this.bgmMode === 'random' && this.bgmOn) this.startRandomTrack()
    })
  }

  private loadThenPlay(key: string, loop: boolean, onComplete?: () => void) {
    if (!this.scene || !this.bgmOn) return
    if (this.scene.cache.audio.exists(key)) { this.doPlay(key, loop, onComplete); return }
    const ver = typeof __ASSET_VER__ !== 'undefined' ? `?v=${__ASSET_VER__}` : ''
    this.scene.load.audio(key, `/assets/audio/${key}.mp3${ver}`)
    this.scene.load.once(`filecomplete-audio-${key}`, () => this.doPlay(key, loop, onComplete))
    this.scene.load.start()
  }

  private doPlay(key: string, loop: boolean, onComplete?: () => void) {
    if (!this.scene || !this.bgmOn) return
    this.bgm?.stop(); this.bgm?.destroy()
    this.bgm = this.scene.sound.add(key, { loop, volume: this.bgmVolume })
    this.currentKey = key
    if (onComplete) this.bgm.once('complete', onComplete)
    this.bgm.play()
  }

  /** 정시 자동 전환(자동 모드 + 현재 시간대 트랙 재생 중일 때만): 30초마다 확인. */
  private ensureHourWatcher() {
    if (this.hourTimer !== undefined) return
    this.hourTimer = setInterval(() => {
      if (!this.bgmOn || !this.scene || this.bgmMode !== 'auto') return
      if (!this.currentKey?.startsWith('bgm_hour_')) return // 결과 음악 등 재생 중이면 건드리지 않음
      const key = `bgm_hour_${String(new Date().getHours()).padStart(2, '0')}`
      if (key !== this.currentKey) this.playHour(new Date().getHours(), true)
    }, 30000)
  }

  /** 설정에서 BGM 모드 선택(자동/수동/랜덤). 꺼져 있으면 켠다. */
  setBgmMode(mode: BgmMode, hour?: number) {
    this.bgmMode = mode
    if (mode === 'manual' && hour != null) this.manualHour = ((hour % 24) + 24) % 24
    try {
      localStorage.setItem(LS_BGM_MODE, mode)
      localStorage.setItem(LS_BGM_HOUR, String(this.manualHour))
    } catch { /* noop */ }
    if (!this.bgmOn) { this.bgmOn = true; try { localStorage.setItem(LS_BGM, 'on') } catch { /* noop */ } }
    this.bgm?.stop() // 모드 변경 → 새로 시작
    this.startBgm()
  }

  setBgm(on: boolean) {
    this.bgmOn = on
    try { localStorage.setItem(LS_BGM, on ? 'on' : 'off') } catch { /* noop */ }
    if (on) {
      this.startBgm()
    } else {
      this.bgm?.stop(); this.bgm?.destroy()
      this.bgm = undefined
      this.currentKey = undefined
    }
  }

  setSfx(on: boolean) {
    this.sfxOn = on
    try { localStorage.setItem(LS_SFX, on ? 'on' : 'off') } catch { /* noop */ }
  }

  setBgmVolume(v: number) {
    this.bgmVolume = clamp01(v)
    try { localStorage.setItem(LS_BGM_VOL, String(this.bgmVolume)) } catch { /* noop */ }
    if (this.bgm && 'setVolume' in this.bgm) (this.bgm as Phaser.Sound.WebAudioSound).setVolume(this.bgmVolume)
  }

  setSfxVolume(v: number) {
    this.sfxVolume = clamp01(v)
    try { localStorage.setItem(LS_SFX_VOL, String(this.sfxVolume)) } catch { /* noop */ }
  }

  toggleBgm() { this.setBgm(!this.bgmOn); return this.bgmOn }
  toggleSfx() { this.setSfx(!this.sfxOn); return this.sfxOn }
}

export const AudioManager = new AudioManagerImpl()
